// BastropRateCard.js - Shareable Sub Rate Card (view-only, no gate)
import React, { useState } from 'react';
import { Download, Printer, ChevronDown, ChevronRight, Sun, Moon } from 'lucide-react';
import { ONBOARDING_CODES } from '../config/constants';
import { getTheme, useMobile } from '../utils/theme';

function BastropRateCard({ darkMode, setDarkMode }) {
  const [collapsed, setCollapsed] = useState({});
  const isMobile = useMobile();
  const theme = getTheme(darkMode);
  const { bgAlt, cardBg, text: textColor, textMedium: mutedColor, border, accentMarketing, logoLY, logoT, topBarBg, topBarText } = theme.colors;

  const config = ONBOARDING_CODES['bastrop2026'];
  const rates = config.rates || [];

  const toggleCategory = (cat) => {
    setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Group rates by category
  const categories = {};
  rates.forEach(r => {
    if (!categories[r.category]) categories[r.category] = [];
    categories[r.category].push(r);
  });

  const downloadCSV = () => {
    const header = 'Code,Description,UOM,Price,Category\n';
    const rows = rates.map(r => `"${r.code}","${r.description}","${r.uom}","${r.price}","${r.category}"`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'LYT_Bastrop_Sub_Rate_Card.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgAlt }}>
      {/* Top bar */}
      <div style={{ backgroundColor: topBarBg, padding: isMobile ? '8px 16px' : '6px 20px', display: 'flex', justifyContent: 'flex-end' }}>
        {setDarkMode && (
          <button onClick={() => setDarkMode(!darkMode)} style={{ backgroundColor: 'transparent', border: 'none', color: topBarText, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '8px 12px', borderRadius: '6px', minHeight: '44px' }}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span className="hide-mobile">{darkMode ? 'Light' : 'Dark'} Mode</span>
          </button>
        )}
      </div>

      {/* Header */}
      <header style={{ padding: isMobile ? '12px 16px' : '16px 20px', backgroundColor: darkMode ? '#0d1b2a' : '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
        <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700' }}>
          <span style={{ color: logoLY }}>ly</span><span style={{ color: logoT }}>t</span>
          <span style={{ fontWeight: '400', fontSize: isMobile ? '0.9rem' : '1rem', marginLeft: '6px', color: textColor }}>Communications</span>
        </div>
      </header>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: isMobile ? '16px' : '32px 20px' }}>
        {/* Title card */}
        <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: isMobile ? '20px 16px' : '28px 32px', marginBottom: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <h1 style={{ color: textColor, fontSize: isMobile ? '1.3rem' : '1.6rem', margin: '0 0 8px', fontWeight: 700 }}>
            Bastrop Fiber — Sub Rate Card
          </h1>
          <p style={{ color: mutedColor, margin: '0 0 4px', fontSize: '0.9rem' }}>
            <strong>Client:</strong> {config.client} &nbsp;|&nbsp; <strong>Project:</strong> {config.project}
          </p>
          <p style={{ color: mutedColor, margin: '0 0 16px', fontSize: '0.85rem' }}>
            <strong>Payment Terms:</strong> {config.paymentTerms}
            {config.retainageTerms && <> &nbsp;|&nbsp; <strong>Retainage:</strong> {config.retainageTerms}</>}
          </p>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={downloadCSV} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: accentMarketing, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>
              <Download size={16} /> Download CSV
            </button>
            <button onClick={() => window.print()} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: 'transparent', color: accentMarketing, border: `1px solid ${accentMarketing}`, borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>
              <Printer size={16} /> Print / PDF
            </button>
          </div>
        </div>

        {/* Rate table by category */}
        {Object.entries(categories).map(([category, items]) => {
          const isCollapsed = collapsed[category];
          return (
            <div key={category} style={{ backgroundColor: cardBg, borderRadius: '12px', marginBottom: '12px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '14px 16px' : '16px 24px', backgroundColor: darkMode ? '#112240' : '#f1f5f9', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isCollapsed ? <ChevronRight size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
                  <span style={{ fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 700, color: textColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {category}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: mutedColor, fontWeight: 400 }}>({items.length} items)</span>
                </div>
              </button>

              {/* Table */}
              {!isCollapsed && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.8rem' : '0.88rem' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${border}` }}>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 24px', textAlign: 'left', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Code</th>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 16px', textAlign: 'left', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Description</th>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 16px', textAlign: 'center', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>UOM</th>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 24px', textAlign: 'right', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={item.code} style={{ borderBottom: idx < items.length - 1 ? `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` : 'none' }}>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 24px', color: accentMarketing, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: isMobile ? '0.75rem' : '0.82rem' }}>{item.code}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 16px', color: textColor, lineHeight: 1.4 }}>{item.description}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 16px', textAlign: 'center', color: mutedColor, fontWeight: 500 }}>{item.uom}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 24px', textAlign: 'right', color: textColor, fontWeight: 700, whiteSpace: 'nowrap' }}>{item.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* Footer note */}
        <div style={{ textAlign: 'center', marginTop: '24px', padding: '16px', color: mutedColor, fontSize: '0.8rem' }}>
          <p style={{ margin: '0 0 4px' }}>Rates effective 2026. All prices are per-unit subcontractor rates.</p>
          <p style={{ margin: 0 }}>Questions? Contact <a href="mailto:matt@lytcomm.com" style={{ color: accentMarketing }}>matt@lytcomm.com</a></p>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default BastropRateCard;
